import React, { useState, useEffect, ReactNode, useMemo, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import AuthContext, { User, UserProfileData } from './AuthContextStore';
import {
  authSignup,
  authVerifyEmail,
  authLogin,
  AiSyncMigrationRequiredError,
  authMfaVerify,
  authRefreshToken,
  authSsoInit,
  authAcceptEula,
} from '@api/auth';
import type { LoginResponse } from '@api/types/auth';
import { completeSso, getSsoConfig } from '@api/sso';
import { forgetReturnTo, ssoLoginPath } from '@utils/ssoSession';
import { pendingAppStarts } from '@utils/pendingAppStarts';
import {
  setSessionCookie,
  getSessionCookie,
  clearSessionCookie,
  setRefreshCookie,
  getRefreshCookie,
  clearRefreshCookie,
  extractCognitoUsername,
} from '@utils/sessionCookie';
import { STORAGE_KEYS } from '@constants/index';
import {
  buildUserFromResponse,
  getStoredUser,
  persistUserToStorage,
  clearStoredUser,
} from '@utils/userSession';
import { setDatadogUser, clearDatadogUser, trackFunnelEvent } from '@utils/datadog';
import { clearOnboardingDraft } from '../pages/Onboarding/useOnboardingDraft';

// A signed-out visitor's way back into a shared app must not outlive the session on a shared computer.
const forgetAppLinkSession = () => {
  forgetReturnTo();
  pendingAppStarts.clearAll().catch(() => undefined);
};

const COOKIE_POLL_INTERVAL_MS = 3000;

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [camLoginUrl, setCamLoginUrl] = useState<string | null>(null);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [mfaEmail, setMfaEmail] = useState<string | null>(null);
  const [isCognitoMfa, setIsCognitoMfa] = useState(false);
  const [pendingRefreshToken, setPendingRefreshToken] = useState<string | null>(null);

  // ── Persist user to state + localStorage in one place ────────────────
  const persistUser = useCallback((userData: User) => {
    setUser(userData);
    persistUserToStorage(userData);
  }, []);

  // ── Session restoration on mount ─────────────────────────────────────
  // SECURITY: never trust stale localStorage for identity fields (orgRole,
  // email, billingStatus…). Always re-validate against the backend via
  // authSsoInit, which returns a fresh LoginResponse for the cookie owner.
  useEffect(() => {
    const cookieToken = getSessionCookie();
    const refreshCookieToken = getRefreshCookie();

    if (!cookieToken && !refreshCookieToken) {
      clearStoredUser();
      setIsInitializing(false);
      return;
    }

    // Carry forward the pending-invite token if present
    const storedPendingInvite = (() => {
      const explicit = localStorage.getItem(STORAGE_KEYS.PENDING_INVITE_TOKEN);
      if (explicit) return explicit;
      const stored = getStoredUser();
      return stored?.pendingInviteToken ?? null;
    })();

    const handleSsoSuccess = (data: Awaited<ReturnType<typeof authSsoInit>>): boolean => {
      if (!data.userId || !data.email || !data.accessToken) {
        console.error('[AuthContext] SSO init returned incomplete data');
        clearStoredUser();
        return false;
      }
      const storedUser = getStoredUser();
      const resolvedRefresh = data.refreshToken ?? refreshCookieToken ?? storedUser?.refreshToken ?? null;
      const userData = buildUserFromResponse(data, {
        pendingInviteToken: storedPendingInvite,
        refreshToken: resolvedRefresh,
      });
      persistUser(userData);
      setSessionCookie(data.accessToken);
      if (data.refreshToken) {
        setRefreshCookie(data.refreshToken);
      }
      return true;
    };

    const attemptRefresh = async (): Promise<boolean> => {
      if (!refreshCookieToken) return false;

      // Cognito REFRESH_TOKEN_AUTH requires the pool username (sub UUID)
      // for SECRET_HASH — NOT the email. Extract from the access token JWT.
      const cognitoUsername =
        (cookieToken ? extractCognitoUsername(cookieToken) : null)
        ?? extractCognitoUsername(getStoredUser()?.token ?? '')
        ?? null;
      if (!cognitoUsername) return false;

      const email = getStoredUser()?.email ?? null;
      const tokenData = await authRefreshToken(refreshCookieToken, cognitoUsername, email ?? undefined);
      setSessionCookie(tokenData.accessToken);
      const data = await authSsoInit(tokenData.accessToken);
      return handleSsoSuccess(data);
    };

    // Always validate with the backend — this is the source of truth for
    // userId, email, orgRole, billingStatus, EULA, MFA enforcement, etc.
    const initSession = async () => {
      try {
        if (cookieToken) {
          const data = await authSsoInit(cookieToken);
          if (handleSsoSuccess(data)) return;
          throw new Error('SSO init returned incomplete data');
        }
        // No session cookie — try refresh directly
        if (await attemptRefresh()) return;
        clearStoredUser();
      } catch (err) {
        console.warn('[AuthContext] SSO init failed, attempting refresh…', (err as Error)?.message);
        try {
          if (await attemptRefresh()) return;
        } catch (error_) {
          console.error('[AuthContext] SSO refresh failed:', (error_ as Error)?.message);
        }
        const reauthProvider = getStoredUser()?.ssoProvider;
        clearStoredUser();
        if (reauthProvider) globalThis.location.assign(ssoLoginPath(reauthProvider));
      } finally {
        setIsInitializing(false);
      }
    };

    void initSession();
  }, [persistUser]);

  const signup = useCallback(async (email: string, password: string, displayName?: string, turnstileToken?: string) => {
    setIsLoading(true);
    setError(null);
    setCamLoginUrl(null);
    try {
      const result = await authSignup(email, password, displayName, turnstileToken);
      return { status: result.status };
    } catch (err) {
      if (err instanceof AiSyncMigrationRequiredError) {
        setCamLoginUrl(err.camLoginUrl);
        setError(err.message);
      } else {
        const msg = err instanceof Error ? err.message : 'Signup failed';
        setError(msg);
      }
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const verifyEmail = useCallback(async (email: string, code: string) => {
    setIsLoading(true);
    setError(null);
    try {
      await authVerifyEmail(email, code);
      trackFunnelEvent('os_account_created');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Verification failed';
      setError(msg);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const establishSession = useCallback(
    (data: LoginResponse, extras?: Partial<User>, fallbackRefreshToken?: string | null) => {
      const pendingToken = localStorage.getItem(STORAGE_KEYS.PENDING_INVITE_TOKEN);
      persistUser(buildUserFromResponse(data, { pendingInviteToken: pendingToken || null, ...extras }));
      if (pendingToken) localStorage.removeItem(STORAGE_KEYS.PENDING_INVITE_TOKEN);
      setSessionCookie(data.accessToken!);
      const refreshToken = data.refreshToken ?? fallbackRefreshToken;
      if (refreshToken) setRefreshCookie(refreshToken);
    },
    [persistUser],
  );

  const login = useCallback(async (email: string, password: string) => {
    setIsLoading(true);
    setError(null);
    setMfaRequired(false);
    setMfaToken(null);
    setMfaEmail(null);
    setIsCognitoMfa(false);

    try {
      const data = await authLogin(email, password);

      if (data.mfaRequired) {
        setMfaRequired(true);
        setMfaToken(data.mfaToken);
        setMfaEmail(email);
        setIsCognitoMfa(data.isCognitoMfa);
        setPendingRefreshToken(data.refreshToken ?? null);
        return;
      }

      if (!data.userId || !data.email || !data.accessToken) {
        throw new Error('Incomplete login response from server');
      }

      establishSession(data);
    } catch (err) {
      if (err instanceof AiSyncMigrationRequiredError) {
        setError(err.message);
        setCamLoginUrl(err.camLoginUrl);
      } else {
        const msg = err instanceof Error ? err.message : 'Login failed';
        setError(msg);
      }
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [establishSession]);

  const completeMfa = useCallback(async (code: string) => {
    if (!mfaToken || !mfaEmail) {
      setError('MFA session missing');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const data = await authMfaVerify(mfaToken, code, mfaEmail, isCognitoMfa);

      if (!data.userId || !data.email || !data.accessToken) {
        throw new Error('Incomplete MFA verification response from server');
      }

      establishSession(data, { mfaEnabled: data.mfaEnabled ?? true }, pendingRefreshToken);
      setMfaRequired(false);
      setMfaToken(null);
      setMfaEmail(null);
      setIsCognitoMfa(false);
      setPendingRefreshToken(null);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'MFA verification failed';
      setError(msg);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [mfaToken, mfaEmail, isCognitoMfa, pendingRefreshToken, establishSession]);

  const completeSsoLogin = useCallback(async (code: string, state: string) => {
    const data = await completeSso(code, state);
    if (!data.userId || !data.email || !data.accessToken) {
      throw new Error('Incomplete single sign-on response from server');
    }
    establishSession(data);
  }, [establishSession]);

  const refreshSession = useCallback(async () => {
    if (!user?.refreshToken) return;
    try {
      const cognitoUsername = extractCognitoUsername(user.token);
      if (!cognitoUsername) throw new Error('Cannot determine Cognito username from stored token');
      const tokenData = await authRefreshToken(user.refreshToken, cognitoUsername, user.email);
      setSessionCookie(tokenData.accessToken);

      // Re-validate the full user profile with the fresh token so orgRole,
      // billingStatus, etc. are never stale.
      try {
        const profile = await authSsoInit(tokenData.accessToken);
        if (profile.userId && profile.email && profile.accessToken) {
          const refreshedUser = buildUserFromResponse(profile, {
            pendingInviteToken: user.pendingInviteToken,
          });
          persistUser(refreshedUser);
          return;
        }
      } catch {
        // SSO init failed — fall back to updating only the token
      }

      const updatedUser = { ...user, token: tokenData.accessToken };
      persistUser(updatedUser);
    } catch {
      // Refresh failed — log out
      setUser(null);
      clearStoredUser();
      clearSessionCookie();
      clearRefreshCookie();
      forgetAppLinkSession();
    }
  }, [user, persistUser]);

  const logout = useCallback(async () => {
    const ssoProvider = user?.ssoProvider;
    if (user?.id) {
      clearOnboardingDraft(user.id);
    }
    setUser(null);
    setMfaRequired(false);
    setMfaToken(null);
    setMfaEmail(null);
    setIsCognitoMfa(false);
    setPendingRefreshToken(null);
    clearStoredUser();
    clearSessionCookie();
    clearRefreshCookie();
    forgetAppLinkSession();
    setError(null);
    queryClient.clear();
    if (ssoProvider) {
      const { logoutUrl } = await getSsoConfig();
      if (logoutUrl) globalThis.location.assign(logoutUrl);
    }
  }, [queryClient, user?.id, user?.ssoProvider]);

  const updateMfaEnabled = useCallback((enabled: boolean) => {
    if (!user) return;
    const updatedUser = { ...user, mfaEnabled: enabled };
    setUser(updatedUser);
    persistUserToStorage(updatedUser);
  }, [user]);

  const updateBillingStatus = useCallback((status: 'PENDING' | 'ACTIVE' | 'SUSPENDED') => {
    if (!user) return;
    const updatedUser = { ...user, billingStatus: status };
    setUser(updatedUser);
    persistUserToStorage(updatedUser);
  }, [user]);

  const markOnboardingComplete = useCallback((data: UserProfileData | null, skipped: boolean) => {
    if (!user) return;
    const updatedUser: User = {
      ...user,
      profileData: data,
      hasCompletedOnboarding: true,
      onboardingSkipped: skipped,
    };
    setUser(updatedUser);
    persistUserToStorage(updatedUser);
  }, [user]);

  const clearPendingInvite = useCallback(() => {
    if (!user) return;
    const updatedUser = { ...user, pendingInviteToken: null };
    setUser(updatedUser);
    persistUserToStorage(updatedUser);
    localStorage.removeItem(STORAGE_KEYS.PENDING_INVITE_TOKEN);
  }, [user]);

  const acceptEula = useCallback(async () => {
    if (!user?.token) return;
    setIsLoading(true);
    setError(null);
    try {
      await authAcceptEula(user.token);
      const updatedUser = { ...user, eulaAccepted: true };
      setUser(updatedUser);
      persistUserToStorage(updatedUser);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to accept EULA';
      setError(msg);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  // Keep Datadog RUM user + org context in sync with the auth state so session
  // replays are attributed to the correct user across all auth paths:
  // login, MFA, page-refresh restore, token refresh, and logout.
  useEffect(() => {
    if (user) {
      setDatadogUser(user);
    } else {
      clearDatadogUser();
    }
  }, [user]);

  // Returns true and re-asserts the shared SSO cookie when our own session is
  // still valid; false when it is genuinely gone (caller should log out).
  const restoreSharedSession = useCallback(async (): Promise<boolean> => {
    if (!user) return false;
    const refreshTok = user.refreshToken ?? getRefreshCookie();
    const cognitoUsername = extractCognitoUsername(user.token);
    if (!refreshTok || !cognitoUsername) return false;
    try {
      const { accessToken } = await authRefreshToken(refreshTok, cognitoUsername, user.email);
      setSessionCookie(accessToken);
      persistUser({ ...user, token: accessToken });
      return true;
    } catch {
      return false;
    }
  }, [user, persistUser]);

  // Cross-product logout detection (AIB-6709): the shared SSO cookie can briefly
  // vanish or be re-scoped while CAM bootstraps in another tab, so an empty read
  // is ambiguous. Re-validate our own session before tearing OS down — only log
  // out if the refresh fails (genuine remote sign-out).
  useEffect(() => {
    if (!user) return;

    let revalidating = false;
    const interval = setInterval(() => {
      if (getSessionCookie() || revalidating) return;
      revalidating = true;
      restoreSharedSession()
        .then((alive) => {
          if (!alive) {
            clearInterval(interval);
            logout();
          }
        })
        .finally(() => {
          revalidating = false;
        });
    }, COOKIE_POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [user, logout, restoreSharedSession]);

  const clearError = useCallback(() => {
    setError(null);
    setCamLoginUrl(null);
  }, []);

  const contextValue = useMemo(() => ({
    user,
    signup,
    verifyEmail,
    login,
    completeMfa,
    completeSsoLogin,
    logout,
    refreshSession,
    acceptEula,
    updateMfaEnabled,
    updateBillingStatus,
    markOnboardingComplete,
    clearPendingInvite,
    isLoading,
    isInitializing,
    error,
    camLoginUrl,
    clearError,
    mfaRequired,
    mfaToken,
    mfaEmail,
    isCognitoMfa,
  }), [user, signup, verifyEmail, login, completeMfa, completeSsoLogin, logout, refreshSession, acceptEula,
       updateMfaEnabled, updateBillingStatus, markOnboardingComplete, clearPendingInvite,
       isLoading, isInitializing,
       error, camLoginUrl, clearError, mfaRequired, mfaToken, mfaEmail, isCognitoMfa]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};
